import { useMutation, useQueryClient } from '@tanstack/react-query';
import partition from 'lodash/partition';
import pluralize from 'pluralize';

import { parseApiError } from '@cast/api-client';
import { notify } from '@cast/design-system';
import { apiClient } from '@cast/infrastructure-api';

import { getNodesQueryKey } from 'hooks/queries/nodes';
import { useCurrentOrganizationId } from '@cast/infrastructure-organization';

type Args = {
  clusterId: string;
  nodeNames: string[];
};

export const useKentDeleteNodesMutation = () => {
  const organizationId = useCurrentOrganizationId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clusterId, nodeNames }: Args) =>
      Promise.allSettled(
        nodeNames.map((nodeName) =>
          apiClient.kent.deleteNode({
            organizationId,
            clusterId,
            nodeName,
            force: true,
          })
        )
      ).then((results) =>
        nodeNames.map((nodeName, index) => ({
          nodeName,
          success: results[index].status === 'fulfilled',
        }))
      ),
    onSuccess: (data, { clusterId }) => {
      const [succeeded, failed] = partition(data, (entry) => entry.success);

      if (succeeded.length === data.length) {
        notify.success(
          `Deletion initiated for ${succeeded.length} ${pluralize(
            'node',
            succeeded.length
          )}`
        );
      }

      if (failed.length === data.length) {
        notify.error(
          `Failed to initiate deletion for ${failed.length} ${pluralize(
            'node',
            failed.length
          )}`
        );
      }

      if (succeeded.length && failed.length) {
        notify.warning(
          `Deletion initiated for ${succeeded.length} out of ${
            data.length
          } ${pluralize('node', data.length)}`
        );
      }

      return queryClient.invalidateQueries({
        queryKey: getNodesQueryKey({ clusterId }),
      });
    },
    onError: (error: unknown) => {
      notify.error(parseApiError(error, 'Failed to initiate nodes deletion'));
    },
  });
};
